// FAQ — perguntas frequentes, agrupadas por categoria + busca server-side.
//
// O endpoint /api/student/faq agora retorna entries + categories (hierarquia
// via parentId). Quando há query, manda no GET via ?q=... e o server filtra
// por ILIKE em question+answer. Entries sem categoryId aparecem em "Outras".
//
// Markdown leve nas respostas via renderAiMarkdown.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FolderClosed,
  FolderOpen,
  HelpCircle,
  Loader2,
  RefreshCw,
  Search,
  X
} from "lucide-react";
import {
  api,
  type FaqStudentCategory,
  type FaqStudentEntry
} from "./api";
import { renderAiMarkdown } from "./aiMarkdown";

interface FaqState {
  phase: "idle" | "loading" | "ready" | "error";
  isEnabled: boolean;
  entries: FaqStudentEntry[];
  categories: FaqStudentCategory[];
  errMsg: string | null;
  lastLoadedAt: number;
  query: string;
}

export const faqState = signal<FaqState>({
  phase: "idle",
  isEnabled: false,
  entries: [],
  categories: [],
  errMsg: null,
  lastLoadedAt: 0,
  query: ""
});

export async function loadFaq(query = ""): Promise<void> {
  faqState.value = {
    ...faqState.value,
    phase: "loading",
    errMsg: null,
    query
  };
  try {
    const path = query
      ? `/api/student/faq?q=${encodeURIComponent(query)}`
      : "/api/student/faq";
    const res = await fetch(path, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as {
      faq: {
        isEnabled: boolean;
        entries: FaqStudentEntry[];
        categories: FaqStudentCategory[];
      };
    };
    faqState.value = {
      phase: "ready",
      isEnabled: data.faq.isEnabled,
      entries: data.faq.entries,
      categories: data.faq.categories,
      errMsg: null,
      lastLoadedAt: Date.now(),
      query
    };
  } catch (err) {
    faqState.value = {
      ...faqState.value,
      phase: "error",
      errMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

// Debounce simples — evita request por keystroke.
let searchTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedSearch(q: string) {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => void loadFaq(q.trim()), 300);
}

export function FaqView() {
  const state = faqState.value;
  const [inputValue, setInputValue] = useState(state.query);
  const [openId, setOpenId] = useState<string | null>(null);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (state.phase === "idle") void loadFaq();
  }, []);

  // Agrupa entries por categoria (root level — sub-cats aparecem como tags por enquanto)
  const grouped = useMemo(() => {
    const rootCats = state.categories
      .filter((c) => c.parentId === null)
      .sort((a, b) => a.position - b.position);
    const childrenOf = (parentId: string) =>
      state.categories.filter((c) => c.parentId === parentId);

    type Group = {
      category: FaqStudentCategory | null;
      entries: FaqStudentEntry[];
      subgroups: { category: FaqStudentCategory; entries: FaqStudentEntry[] }[];
    };

    const groups: Group[] = rootCats.map((cat) => {
      const subs = childrenOf(cat.id).sort((a, b) => a.position - b.position);
      const subIds = new Set(subs.map((s) => s.id));
      const direct = state.entries.filter((e) => e.categoryId === cat.id);
      const subgroups = subs.map((sub) => ({
        category: sub,
        entries: state.entries.filter((e) => e.categoryId === sub.id)
      }));
      return {
        category: cat,
        entries: direct,
        subgroups: subgroups.filter((sg) => sg.entries.length > 0)
      };
    });

    // Órfãs (sem categoryId, ou categoryId não corresponde a nenhuma cat conhecida)
    const knownIds = new Set(state.categories.map((c) => c.id));
    const orphans = state.entries.filter(
      (e) => !e.categoryId || !knownIds.has(e.categoryId)
    );
    if (orphans.length > 0) {
      groups.push({ category: null, entries: orphans, subgroups: [] });
    }

    return groups.filter(
      (g) => g.entries.length > 0 || g.subgroups.length > 0
    );
  }, [state.entries, state.categories]);

  function toggleCat(id: string) {
    setCollapsedCats((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <HelpCircle className="size-6 text-primary" />
            Perguntas frequentes
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Respostas pras dúvidas mais comuns, organizadas por categoria. Não
            achou o que procura? Reporta uma sugestão em "Reportar algo".
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadFaq(state.query)}
          disabled={state.phase === "loading"}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-60"
          title="Atualizar"
        >
          <RefreshCw width="1.1em" height="1.1em" />
          Atualizar
        </button>
      </header>

      <div class="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={inputValue}
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setInputValue(v);
            debouncedSearch(v);
          }}
          placeholder="Buscar nas perguntas e respostas…"
          class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
        />
        {inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue("");
              void loadFaq("");
            }}
            aria-label="Limpar busca"
            class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {state.phase === "loading" && (
        <div class="flex items-center justify-center gap-2 rounded-md border border-border bg-card/40 px-4 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando…
        </div>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div class="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
            <div class="space-y-1.5">
              <h3 class="text-sm font-bold">Não consegui carregar o FAQ</h3>
              {state.errMsg && (
                <p class="text-xs text-muted-foreground">{state.errMsg}</p>
              )}
              <button
                type="button"
                onClick={() => void loadFaq()}
                class="mt-1 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      )}

      {state.phase === "ready" &&
        state.entries.length === 0 &&
        !state.query && (
          <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-12 text-center">
            <HelpCircle className="mx-auto mb-2 size-8 text-muted-foreground/50" />
            <h3 class="text-sm font-bold">Ainda não há perguntas publicadas</h3>
            <p class="mt-1 text-xs text-muted-foreground">
              Volte mais tarde — a equipe está montando o conteúdo.
            </p>
          </div>
        )}

      {state.phase === "ready" &&
        state.entries.length === 0 &&
        state.query && (
          <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-10 text-center">
            <Search className="mx-auto mb-2 size-6 text-muted-foreground/50" />
            <h3 class="text-sm font-bold">Nada para "{state.query}"</h3>
            <p class="mt-1 text-xs text-muted-foreground">
              Tente outras palavras ou limpe a busca pra ver tudo.
            </p>
          </div>
        )}

      {state.phase === "ready" && grouped.length > 0 && (
        <div class="space-y-4">
          {grouped.map((g) => {
            const catId = g.category?.id ?? "__orphans__";
            const catName = g.category?.name ?? "Outras";
            const isCollapsed = collapsedCats.has(catId);
            const totalEntries =
              g.entries.length +
              g.subgroups.reduce((s, sg) => s + sg.entries.length, 0);

            return (
              <section
                key={catId}
                class="rounded-lg border border-border bg-card/40"
              >
                <button
                  type="button"
                  onClick={() => toggleCat(catId)}
                  class="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-accent/30"
                  {...(isCollapsed
                    ? ({ "aria-expanded": "false" } as const)
                    : ({ "aria-expanded": "true" } as const))}
                >
                  {isCollapsed ? (
                    <ChevronRight className="size-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="size-4 text-muted-foreground" />
                  )}
                  {isCollapsed ? (
                    <FolderClosed className="size-4 text-primary" />
                  ) : (
                    <FolderOpen className="size-4 text-primary" />
                  )}
                  <span class="flex-1 font-bold">{catName}</span>
                  <span class="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {totalEntries}
                  </span>
                </button>

                {!isCollapsed && (
                  <div class="space-y-1 border-t border-border px-2 py-2">
                    {g.entries.map((e) => (
                      <FaqItem
                        key={e.id}
                        entry={e}
                        open={openId === e.id}
                        onToggle={() =>
                          setOpenId(openId === e.id ? null : e.id)
                        }
                      />
                    ))}
                    {g.subgroups.map((sg) => (
                      <div key={sg.category.id} class="px-2 pb-2 pt-3">
                        <div class="mb-1 px-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          {sg.category.name}
                        </div>
                        {sg.entries.map((e) => (
                          <FaqItem
                            key={e.id}
                            entry={e}
                            open={openId === e.id}
                            onToggle={() =>
                              setOpenId(openId === e.id ? null : e.id)
                            }
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FaqItem({
  entry,
  open,
  onToggle
}: {
  entry: FaqStudentEntry;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div class="overflow-hidden rounded-md border border-border/60 bg-background/40">
      <button
        type="button"
        onClick={onToggle}
        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/40"
        {...(open
          ? ({ "aria-expanded": "true" } as const)
          : ({ "aria-expanded": "false" } as const))}
      >
        <span class="flex-1 font-semibold text-foreground">
          {entry.question}
        </span>
        <ChevronDown
          className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div class="border-t border-border/60 bg-background/60 px-3 py-3">
          <div
            class="prose prose-sm max-w-none text-foreground prose-a:text-primary prose-a:underline prose-strong:text-foreground prose-code:text-foreground"
            dangerouslySetInnerHTML={{ __html: renderAiMarkdown(entry.answer) }}
          />
        </div>
      )}
    </div>
  );
}
